<template>
    <div>
        <div class="card border-0 shadow-sm">
            <div class="card-body p-4">
                <div class="d-flex justify-content-between align-items-center mb-4">
                    <h5 class="mb-0">API Keys</h5>
                    <button class="btn btn-primary btn-sm" @click="showCreateModal = true">
                        Generate API Key
                    </button>
                </div>

                <div v-if="loading" class="text-center py-4">
                    <app-overlay-loader/>
                </div>

                <div v-else-if="keys.length === 0" class="text-center text-muted py-4">
                    <app-icon name="key" class="size-40 mb-2 text-muted"/>
                    <p>No API keys yet. Generate one to get started.</p>
                </div>

                <table v-else class="table table-hover">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Prefix</th>
                            <th>Scopes</th>
                            <th>Created</th>
                            <th>Last Used</th>
                            <th>Status</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="key in keys" :key="key.id">
                            <td class="font-weight-bold">{{ key.name }}</td>
                            <td><code>{{ key.prefix }}...</code></td>
                            <td>
                                <span v-for="s in (key.scopes || []).slice(0, 3)" :key="s"
                                      class="badge badge-light border mr-1">{{ s }}</span>
                                <span v-if="(key.scopes || []).length > 3" class="badge badge-light border">
                                    +{{ key.scopes.length - 3 }}
                                </span>
                            </td>
                            <td class="text-muted font-size-90">{{ formatDate(key.created_at) }}</td>
                            <td class="text-muted font-size-90">{{ key.last_used_at ? formatDate(key.last_used_at) : 'Never' }}</td>
                            <td>
                                <span :class="key.is_active ? 'badge badge-success' : 'badge badge-secondary'">
                                    {{ key.is_active ? 'Active' : 'Revoked' }}
                                </span>
                            </td>
                            <td>
                                <button v-if="key.is_active" class="btn btn-sm btn-outline-danger"
                                        @click="revokeKey(key.id)">Revoke</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <!-- Create Modal -->
        <app-modal v-if="showCreateModal" modal-id="create-api-key" @close-modal="showCreateModal = false" modal-size="default">
            <div class="p-4">
                <h5 class="mb-3">Generate API Key</h5>

                <div v-if="createdKey" class="mb-3">
                    <div class="alert alert-success">
                        <strong>Key created!</strong> Copy it now — it will not be shown again.
                    </div>
                    <div class="d-flex align-items-center mb-2">
                        <code class="p-2 bg-light rounded flex-grow-1 mr-2" style="word-break:break-all;">{{ createdKey }}</code>
                        <button class="btn btn-sm btn-primary" @click="copyKey">Copy</button>
                    </div>
                    <button class="btn btn-secondary btn-sm mt-2" @click="closeCreateModal">Done</button>
                </div>

                <form v-else @submit.prevent="createKey">
                    <div class="form-group">
                        <label>Name</label>
                        <input type="text" v-model="form.name" class="form-control" placeholder="e.g. Production API" required/>
                    </div>

                    <div class="form-group">
                        <label>Scopes</label>
                        <div class="row">
                            <div v-for="scope in availableScopes" :key="scope" class="col-6 mb-1">
                                <label class="d-flex align-items-center" style="cursor:pointer;">
                                    <input type="checkbox" :value="scope" v-model="form.scopes" class="mr-2"/>
                                    <code class="font-size-90">{{ scope }}</code>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="form-group">
                        <label>Expires</label>
                        <select v-model="form.expires_in" class="form-control">
                            <option value="">Never</option>
                            <option value="30d">30 days</option>
                            <option value="90d">90 days</option>
                            <option value="1y">1 year</option>
                        </select>
                    </div>

                    <button type="submit" class="btn btn-primary" :disabled="creating || !form.name || !form.scopes.length">
                        {{ creating ? 'Generating...' : 'Generate Key' }}
                    </button>
                </form>
            </div>
        </app-modal>
    </div>
</template>

<script>
import {axiosGet, axiosPost, axiosDelete} from "../../../Helpers/AxiosHelper";

export default {
    name: "ApiKeySettings",
    props: ["props"],
    data() {
        return {
            loading: true,
            keys: [],
            showCreateModal: false,
            creating: false,
            createdKey: '',
            form: {
                name: '',
                scopes: [],
                expires_in: '',
            },
            availableScopes: [
                'read:contacts', 'write:contacts', 'delete:contacts',
                'read:deals', 'write:deals', 'delete:deals',
                'read:pipelines', 'write:pipelines',
                'read:organizations', 'write:organizations', 'delete:organizations',
                'read:activities', 'write:activities',
                'read:proposals', 'write:proposals',
                'read:invoices', 'write:invoices',
                'read:reports',
                'admin:users', 'admin:settings',
            ],
        }
    },
    created() {
        this.fetchKeys();
    },
    methods: {
        fetchKeys() {
            this.loading = true;
            axiosGet('api-keys')
                .then(({data}) => { this.keys = data.data || []; })
                .catch(() => { this.keys = []; })
                .finally(() => { this.loading = false; });
        },
        createKey() {
            this.creating = true;
            axiosPost('api-keys', this.form)
                .then(({data}) => {
                    this.createdKey = data.key;
                    this.fetchKeys();
                })
                .catch(({response}) => {
                    this.$toastr.e(response?.data?.message || 'Failed to create key');
                })
                .finally(() => { this.creating = false; });
        },
        revokeKey(id) {
            if (!confirm('Revoke this API key? This cannot be undone.')) return;
            axiosDelete('api-keys/' + id)
                .then(() => {
                    this.$toastr.s('API key revoked');
                    this.fetchKeys();
                });
        },
        copyKey() {
            navigator.clipboard.writeText(this.createdKey);
            this.$toastr.s('Copied to clipboard');
        },
        closeCreateModal() {
            this.showCreateModal = false;
            this.createdKey = '';
            this.form = {name: '', scopes: [], expires_in: ''};
        },
        formatDate(d) {
            if (!d) return '';
            return new Date(d).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'});
        }
    }
}
</script>
